<template>
  <div>
    <div v-if="Object.keys(analyzedData).length">
      <div class="row">
        <label class="col-3"></label>
        <div class="col-9">
          <h5 class="font-weight-bold">
            {{ $t("dynamicModels.analyzeSettings") }}:
          </h5>
        </div>
      </div>
      <!-- START: Config forms -->
      <ConfigCSV
        v-if="analyzedData.config.type === 0"
        ref="step-basic-config-0"
        :initial-config="analyzedData.config"
      />
      <ConfigXML
        v-else-if="analyzedData.config.type === 1"
        ref="step-basic-config-1"
        :initial-config="analyzedData.config"
      />
      <ConfigJSON
        v-else-if="analyzedData.config.type === 2"
        ref="step-basic-config-2"
        :initial-config="analyzedData.config"
      />

      <div v-if="[0, 1, 2].includes(analyzedData.config.type)">
        <div class="form-group row align-items-center">
          <label class="col-3 col-form-label">
            {{ $t("dataStructures.onEmptyData") }}
          </label>
          <div class="col-md-9 col-lg-8 col-xl-4">
            <div class="input-group">
              <v-select
                v-model="onEmptyDataOption"
                class="form-control"
                :items="onEmptyDataOptions"
                item-text="label"
                item-value="value"
                :menu-props="{ offsetY: true }"
              />
            </div>
            <span class="form-text text-muted">{{
              $t("dataStructures.onEmptyDataTooltip")
            }}</span>
          </div>
        </div>

        <div class="form-group row align-items-center">
          <label class="col-3 col-form-label">
            {{ $t("dataStructures.ttl") }}
          </label>
          <div class="col-md-9 col-lg-8 col-xl-4">
            <div class="input-group">
              <v-select
                v-model="ttlOption"
                class="form-control"
                :items="ttlOptions"
                item-text="label"
                item-value="value"
                :menu-props="{ offsetY: true }"
              />
            </div>
            <span class="form-text text-muted">{{
              $t("dataStructures.ttlTooltip")
            }}</span>
          </div>
        </div>
      </div>

      <!-- END: Config forms -->
      <div class="row">
        <label class="col-3"></label>
        <div class="col-9">
          <button
            v-b-popover.hover.bottom.v-dark
            class="btn btn-primary mt-1"
            :title="$t('dynamicModels.resetsTableConfig')"
            @click="analyzeFile"
          >
            {{ $t("dynamicModels.reanalyzeFile") }}
          </button>
        </div>
      </div>
      <div class="separator separator-dashed my-5"></div>
      <div class="form-group row align-items-center">
        <label class="col-xl-3 col-lg-3 col-form-label">
          <div class="">{{ $t("dynamicModels.showPreview") }}</div>
        </label>
        <div class="col-md-9 col-lg-8 col-xl-9">
          <div class="d-flex justify-space-between align-items-center">
            <span class="switch">
              <label>
                <input v-model="showPreview" type="checkbox" />
                <span></span>
              </label>
            </span>
            <i
              v-b-popover.hover.top="$t('dynamicModels.showPreviewHelp')"
              class="fal fa-question-circle ml-1"
            />
          </div>
        </div>
      </div>
      <div v-if="showPreview">
        <!-- START: Previews -->
        <PreviewCSV
          v-if="analyzedData.config.type === 0"
          :fields="analyzedData.fields"
          :items="analyzedData.entries"
          :rows="analyzedData.config.entryQuantity"
        />
        <PreviewXML
          v-else-if="analyzedData.config.type === 1"
          :items="analyzedData.entries"
          :primary="analyzedData.primary"
          @primary-changed="setPrimaryKey"
        />
        <PreviewJSON
          v-else-if="analyzedData.config.type === 2"
          :items="analyzedData.entries"
          :preview-lines="
            analyzedData.config.previewLines
              ? analyzedData.config.previewLines
              : 10
          "
        />
        <!-- END: Previews -->
      </div>
      <div
        v-if="!showPreview && analyzedData.config.type === 1"
        class="form-group row align-items-center"
      >
        <label class="col-xl-3 col-lg-3 col-form-label">
          <div class="">{{ $t("dynamicModels.primaryKey") }}</div>
        </label>
        <div class="col-md-9 col-lg-8 col-xl-9">
          <div class="d-flex align-items-center">
            <div class="input-group mr-1">
              <input
                v-model="analyzedData.primary"
                type="text"
                class="form-control"
              />
            </div>
            <i
              v-b-popover.hover.top="$t('dynamicModels.primaryXmlHelp')"
              class="fal fa-question-circle ml-1"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
// Config templates
import ConfigCSV from "@/components/DataStructures/Configs/CSV.vue";
import ConfigXML from "@/components/DataStructures/Configs/XML.vue";
import ConfigJSON from "@/components/DataStructures/Configs/JSON.vue";
// Preview templates
import PreviewCSV from "@/components/DataStructures/Previews/CSV.vue";
import PreviewXML from "@/components/DataStructures/Previews/XML.vue";
import PreviewJSON from "@/components/DataStructures/Previews/JSON.vue";

export default {
  components: {
    ConfigCSV,
    ConfigXML,
    ConfigJSON,
    PreviewCSV,
    PreviewXML,
    PreviewJSON
  },
  props: ["analyzedData"],
  data() {
    return {
      showPreview: false,
      onEmptyDataOption: {
        label: "-",
        value: ""
      },
      onEmptyDataOptions: [
        {
          label: "-",
          value: ""
        },
        {
          label: "Warnung",
          value: "warn"
        },
        {
          label: "Fehler",
          value: "error"
        }
      ],
      ttlOption: null,
      ttlOptions: [
        {
          label: this.$t("dataStructures.ttlOptions.noExpiration"),
          value: null
        },
        {
          label: this.$t("dataStructures.ttlOptions.24h"),
          value: 86400
        },
        {
          label: this.$t("dataStructures.ttlOptions.1w"),
          value: 604800
        },
        {
          label: this.$t("dataStructures.ttlOptions.2w"),
          value: 1209600
        },
        {
          label: this.$t("dataStructures.ttlOptions.4w"),
          value: 2419200
        },
        {
          label: this.$t("dataStructures.ttlOptions.3m"),
          value: 7776000
        },
        {
          label: this.$t("dataStructures.ttlOptions.1y"),
          value: 31536000
        }
      ]
    };
  },
  computed: {
    acceptFileTypes: function () {
      let types = this.validFileTypes;
      let acceptString = "";
      types.forEach(type => {
        let string = "." + type + ", ";
        acceptString = acceptString.concat(string);
      });
      acceptString = acceptString.slice(0, -2);
      return acceptString;
    }
  },
  watch: {
    onEmptyDataOption() {
      this.analyzedData.config.onEmptyData = this.onEmptyDataOption;
    },
    ttlOption() {
      // Save only to root level, not in config
      this.$set(this.analyzedData, "ttl", this.ttlOption);
      this.$emit("ttl-changed", this.ttlOption);
    }
  },
  methods: {
    onFileInput(file) {
      let filename = file.name;
      let extension = filename.split(".").pop();
      let extensionMode = null;
      switch (extension) {
        case "csv":
          extensionMode = 0;
          break;
        case "xml":
          extensionMode = 1;
          break;
      }
      this.fileType = extensionMode;
    },
    analyzeFile() {
      let config =
        this.$refs[
          "step-basic-config-" + this.analyzedData.config.type
        ].fetchConfig();
      this.$emit("analyze", config);
    },
    getConfig() {
      return this.$refs[
        "step-basic-config-" + this.analyzedData.config.type
      ].fetchConfig();
    },
    setPrimaryKey(primary) {
      this.$set(this.analyzedData, "primary", primary);
    }
  }
};
</script>
